// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconMemo } from "@insite/mobius/Icon";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductTooltip, { ProductTooltipStyles } from "@pace/App/Components/Product/ProductTooltip";
import updateCustomerPartNumber from "@pace/App/Handlers/XNG/UpdateCustomerPartNumber";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import IconCheckmark from "@pace/XDL/Icons/IconCheckmark";
import IconEdit from "@pace/XDL/Icons/IconEdit";
import IconTrash from "@pace/XDL/Icons/IconTrash";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Change
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasProduct & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    // XNG-Change
    valueContainer?: InjectableCss;
    editButton?: ClickablePresentationProps;
    editForm?: InjectableCss;
    cpnField?: InjectableCss;
    formButton?: InjectableCss;
    infoTooltip?: ProductTooltipStyles;
}

// XNG-Change: Add state, dispatch
const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
    customerId: state.context.session?.billToId || "",
    customerProductNumber: state.xProductDetails.customerProductNumber,
});

const mapDispatchToProps = {
    updateCustomerPartNumber,
};

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 15px;
        `,
    },
    valueText: {
        size: 16,
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = customerPartNumberStyles;

// XNG-Change: Add stateful customer part number
// TODO: Check with BE once they connect it to MIS
const ProductDetailsCustomerPartNumber: React.FC<Props> = ({
    product,
    // XNG-Change
    isAuthenticated,
    updateCustomerPartNumber,
    customerId,
    customerProductNumber,
}) => {
    if (!isAuthenticated || !customerId) {
        return null;
    }
    // <XNG-Change>
    const [isEditable, setIsEditable] = React.useState(false);
    const [isUpdating, setIsUpdating] = React.useState(false);

    const toasterContext = React.useContext(ToasterContext);

    const updateCustomerNumber = (value?: string) => {
        if (value === customerProductNumber) {
            setIsEditable(false);
            return;
        }
        setIsUpdating(true);
        updateCustomerPartNumber({
            CustomerId: customerId,
            ProductId: product.id,
            Value: value || "",
            onComplete: result => {
                if (result.apiResult?.error) {
                    toasterContext.addToast({
                        messageType: "danger",
                        body: siteMessage("Custom_ProductDetails_CPN_Update_Error_Message"),
                    });
                }
                setIsUpdating(false);
                setIsEditable(false);
            },
        });
    };
    // </XNG-Change>

    // XNG-Change: Add Editable field to customer
    return (
        <StyledWrapper {...styles.valueContainer} data-test-selector="customerProductNumber">
            {isEditable ? (
                <>
                    <Typography {...styles.labelText}>{translate("CPN")}</Typography>
                    <EditCustomerField
                        value={customerProductNumber}
                        onCancel={() => setIsEditable(false)}
                        onSave={updateCustomerNumber}
                        isLoading={isUpdating}
                    />
                </>
            ) : (
                <StyledWrapper {...styles.cpnField}>
                    <VisuallyHidden>{translate("Edit CPN")}</VisuallyHidden>
                    <ProductTooltip
                        label={translate("CPN")}
                        tooltipText={siteMessage("Custom_CPN_info") as string}
                        onClick={() => setIsEditable(true)}
                        icon={IconEdit}
                        value={customerProductNumber}
                        extendedStyles={styles.infoTooltip}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Change
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsCustomerPartNumber)),
    definition: {
        displayName: "Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;

// <XNG-Change>
interface IEditCustomerProps {
    value?: string;
    onCancel: () => void;
    onSave: (value?: string) => void;
    isLoading?: boolean;
}
const EditCustomerField = ({ value, onCancel, onSave, isLoading }: IEditCustomerProps) => {
    const [customerField, setCustomerField] = React.useState(value);
    React.useEffect(() => {
        setCustomerField(value);
    }, [value]);
    const onDelete = () => onSave("");
    return (
        <StyledWrapper {...styles.editForm}>
            <TextField
                disabled={isLoading}
                value={customerField}
                placeholder="0000"
                onChange={e => setCustomerField(e.target.value)}
                maxLength={30}
            />
            <Button
                disabled={isLoading || !customerField?.length}
                onClick={() => onSave(customerField)}
                aria-label="Add CPN"
                // Xng-Changes
                {...styles.formButton}
                {...{ value }}
            >
                {value ? <IconMemo src={IconCheckmark} size={17} color="common.white" /> : translate("Add")}
            </Button>
            <Button
                disabled={isLoading}
                variant="secondary"
                onClick={value ? onDelete : onCancel}
                aria-label="Cancel"
                // Xng-Changes
                {...styles.formButton}
                {...{ value }}
            >
                {value ? <IconMemo src={IconTrash} size={17} color="common.secondary" /> : translate("Cancel")}
            </Button>
        </StyledWrapper>
    );
};
// </XNG-Chang>
